import React, { useCallback, useEffect, useState } from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  Divider,
  HStack,
  Icon,
  Progress,
  Select,
  Checkbox,
  SimpleGrid,
  Spinner,
  Stat,
  StatHelpText,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useToast,
  useDisclosure,
  IconButton,
  Tooltip,
} from "@chakra-ui/react";
import { DeleteIcon, AddIcon, RepeatIcon, DownloadIcon } from "@chakra-ui/icons";
import {
  acknowledgeComplianceAlert,
  createComplianceOutreachTask,
  documentComplianceChart,
  downloadComplianceExport,
  fetchComplianceAlerts,
  fetchComplianceDashboard,
  fetchComplianceProviders,
  notifyCompliancePatient,
  orderComplianceTest,
  resolveComplianceAlert,
  runComplianceEvaluate,
  fetchMedicationFills,
  deleteMedicationFill,
} from "../services/complianceService";
import ComplianceCareGapsImport from "./ComplianceCareGapsImport";
import ComplianceTasksPanel from "./ComplianceTasksPanel";
import MedicationFillModal from "./MedicationFillModal";

const severityColor = {
  critical: "red",
  high: "orange",
  medium: "yellow",
  low: "gray",
};

const ComplianceQualityDashboard = () => {
  const toast = useToast();
  const { isOpen: isFillOpen, onOpen: onFillOpen, onClose: onFillClose } = useDisclosure();
  
  const [loading, setLoading] = useState(true);
  const [evaluating, setEvaluating] = useState(false);
  const [dashboard, setDashboard] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [actionId, setActionId] = useState(null);
  const [providers, setProviders] = useState([]);
  const [providerFilter, setProviderFilter] = useState("");
  const [deidentifyExport, setDeidentifyExport] = useState(false);
  
  // Fills state
  const [fills, setFills] = useState([]);
  const [loadingFills, setLoadingFills] = useState(false);
  const [deletingFillId, setDeletingFillId] = useState(null);

  const loadFills = useCallback(async () => {
    setLoadingFills(true);
    try {
      const data = await fetchMedicationFills();
      setFills(data.fills || []);
    } catch (err) {
      console.warn("Could not load medication fills:", err);
    } finally {
      setLoadingFills(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const provider = providerFilter || undefined;
      const [dash, alertData, provData] = await Promise.all([
        fetchComplianceDashboard(undefined, { provider }),
        fetchComplianceAlerts({ status: "open", provider }),
        fetchComplianceProviders(),
      ]);
      setDashboard(dash);
      setAlerts(alertData.alerts || []);
      setProviders(provData.providers || []);
      await loadFills();
    } catch (err) {
      toast({
        title: "Could not load compliance data",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }, [toast, providerFilter, loadFills]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const handleEvaluate = async () => {
    setEvaluating(true);
    try {
      const result = await runComplianceEvaluate();
      toast({
        title: "Evaluation complete",
        description: `Scanned patient charts and documents. Found ${result.evaluated} patients, ${result.alerts_created} new alerts.`,
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      await refresh();
    } catch (err) {
      toast({
        title: "Evaluation failed",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setEvaluating(false);
    }
  };

  const runAction = async (id, fn, successMsg) => {
    setActionId(id);
    try {
      await fn(id);
      toast({ title: successMsg, status: "success", duration: 3000 });
      await refresh();
    } catch (err) {
      toast({
        title: "Action failed",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setActionId(null);
    }
  };

  const handleDeleteFill = async (fillId) => {
    setDeletingFillId(fillId);
    try {
      await deleteMedicationFill(fillId);
      toast({
        title: "Medication fill removed",
        status: "info",
        duration: 3000,
      });
      await refresh();
    } catch (err) {
      toast({
        title: "Removal failed",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    } finally {
      setDeletingFillId(null);
    }
  };

  // The export is fetched with the signed-in user's token and saved as a file:
  // a plain link to it would carry none, and the server refuses that.
  const handleExport = async () => {
    try {
      await downloadComplianceExport({
        provider: providerFilter || undefined,
        status: "all",
        deidentify: deidentifyExport,
      });
      toast({
        title: "Export downloaded",
        description: "The payer CSV was saved.",
        status: "success",
        duration: 3000,
      });
    } catch (err) {
      toast({
        title: "Export failed",
        description: err.message,
        status: "error",
        duration: 5000,
      });
    }
  };

  if (loading && !dashboard) {
    return (
      <Box py={20} textAlign="center">
        <Spinner size="xl" color="purple.500" thickness="4px" />
        <Text mt={4} fontSize="lg" fontWeight="medium" color="gray.600">
          Loading clinical quality measures…
        </Text>
      </Box>
    );
  }

  const hedis = dashboard?.hedis_performance_pct ?? 0;
  const starsWeighted = dashboard?.stars_weighted_performance_pct ?? hedis;
  const measures = dashboard?.measures || [];
  const med = dashboard?.medication || {};
  const catalogVersion = dashboard?.catalog_version ?? "2026.1";

  // Filter alerts by severity for display sections
  const criticalAlerts = alerts.filter(a => a.severity === "critical");
  const highAlerts = alerts.filter(a => a.severity === "high");
  const mediumAlerts = alerts.filter(a => a.severity === "medium" || a.severity === "low");

  return (
    <VStack align="stretch" spacing={8} p={1}>
      {/* Sleek Gradient Header Bar */}
      <Box
        bgGradient="linear(135deg, #2D3748 0%, #1A202C 100%)"
        borderRadius="2xl"
        p={6}
        boxShadow="xl"
        color="white"
      >
        <HStack justify="space-between" flexWrap="wrap" gap={4}>
          <Box>
            <HStack mb={1} spacing={3}>
              <Text fontSize="2xl" fontWeight="extrabold" letterSpacing="-0.02em">
                Compliance & Quality Dashboard
              </Text>
              <Badge colorScheme="purple" variant="solid" borderRadius="full" px={3} py={0.5} fontSize="xs">
                Catalog {catalogVersion}
              </Badge>
            </HStack>
            <Text fontSize="sm" color="gray.400">
              Real-time preventive care gaps and medication adherence monitoring for clinical operations.
            </Text>
          </Box>
          <HStack flexWrap="wrap" spacing={3}>
            <Select
              size="md"
              bg="gray.800"
              color="white"
              borderColor="gray.700"
              _hover={{ borderColor: "gray.600" }}
              maxW="220px"
              placeholder="All providers"
              value={providerFilter}
              onChange={(e) => setProviderFilter(e.target.value)}
            >
              {providers.map((p) => (
                <option key={p.provider_name} value={p.provider_name} style={{ color: "black" }}>
                  {p.provider_name} ({p.open} open)
                </option>
              ))}
            </Select>
            <Checkbox
              colorScheme="purple"
              isChecked={deidentifyExport}
              onChange={(e) => setDeidentifyExport(e.target.checked)}
              color="gray.300"
              borderColor="gray.500"
              fontSize="sm"
              fontWeight="medium"
              mr={1}
            >
              De-identify
            </Checkbox>
            <Button
              leftIcon={<DownloadIcon />}
              variant="outline"
              colorScheme="whiteAlpha"
              size="md"
              onClick={handleExport}
            >
              Export CSV
            </Button>
            <Button
              leftIcon={<AddIcon />}
              colorScheme="blue"
              size="md"
              onClick={onFillOpen}
            >
              Add Medication Fill
            </Button>
            <Button
              leftIcon={<RepeatIcon />}
              colorScheme="purple"
              size="md"
              onClick={handleEvaluate}
              isLoading={evaluating}
              loadingText="Scanning Documents"
            >
              Run Gap Scan
            </Button>
          </HStack>
        </HStack>
      </Box>

      {/* Integration & Tasks panels */}
      <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
        <ComplianceCareGapsImport onImported={refresh} />
        <ComplianceTasksPanel />
      </SimpleGrid>

      {/* Premium KPI Row */}
      <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={6}>
        <Stat
          p={5}
          bg="white"
          borderRadius="2xl"
          boxShadow="lg"
          borderLeft="5px solid"
          borderLeftColor="purple.500"
          position="relative"
          overflow="hidden"
          _hover={{ transform: "translateY(-4px)", transition: "all 0.2s" }}
        >
          <StatLabel fontWeight="bold" color="gray.500" fontSize="sm">Stars Weighted Performance</StatLabel>
          <StatNumber fontSize="3xl" fontWeight="black" mt={1}>{starsWeighted}%</StatNumber>
          <StatHelpText mb={4}>CMS measure weights applied</StatHelpText>
          <Progress value={starsWeighted} colorScheme="purple" size="xs" borderRadius="full" />
        </Stat>

        <Stat
          p={5}
          bg="white"
          borderRadius="2xl"
          boxShadow="lg"
          borderLeft="5px solid"
          borderLeftColor="emerald.500"
          position="relative"
          overflow="hidden"
          _hover={{ transform: "translateY(-4px)", transition: "all 0.2s" }}
        >
          <StatLabel fontWeight="bold" color="gray.500" fontSize="sm">HEDIS Closure Rate</StatLabel>
          <StatNumber fontSize="3xl" fontWeight="black" mt={1}>{hedis}%</StatNumber>
          <StatHelpText mb={4}>Unweighted closed vs open</StatHelpText>
          <Progress value={hedis} colorScheme="green" size="xs" borderRadius="full" />
        </Stat>

        <Stat
          p={5}
          bg="white"
          borderRadius="2xl"
          boxShadow="lg"
          borderLeft="5px solid"
          borderLeftColor="orange.500"
          _hover={{ transform: "translateY(-4px)", transition: "all 0.2s" }}
        >
          <StatLabel fontWeight="bold" color="gray.500" fontSize="sm">Open Alerts</StatLabel>
          <StatNumber fontSize="3xl" fontWeight="black" mt={1}>{dashboard?.summary?.open_alerts ?? 0}</StatNumber>
          <StatHelpText mb={1}>
            {providerFilter ? `Panel: ${providerFilter}` : "All active providers"}
          </StatHelpText>
          <HStack spacing={2} mt={2}>
            <Badge colorScheme="red">{criticalAlerts.length} Critical</Badge>
            <Badge colorScheme="orange">{highAlerts.length} High</Badge>
          </HStack>
        </Stat>

        <Stat
          p={5}
          bg="white"
          borderRadius="2xl"
          boxShadow="lg"
          borderLeft="5px solid"
          borderLeftColor="blue.500"
          _hover={{ transform: "translateY(-4px)", transition: "all 0.2s" }}
        >
          <StatLabel fontWeight="bold" color="gray.500" fontSize="sm">Medication Adherence PDC</StatLabel>
          <StatNumber fontSize="3xl" fontWeight="black" mt={1}>{med.resolved ?? 0}</StatNumber>
          <StatHelpText mb={1}>
            Resolved fills · {med.open ?? 0} open gaps
          </StatHelpText>
          <Badge colorScheme="blue" mt={1}>Catalog Wt: {med.stars_weight ?? 15}</Badge>
        </Stat>
      </SimpleGrid>

      {/* Measures Table */}
      <Box bg="white" borderRadius="2xl" boxShadow="xl" p={6}>
        <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.800">
          Measures Catalog & Risk Analysis
        </Text>
        <Box overflowX="auto">
          <Table variant="simple" size="md">
            <Thead bg="gray.50">
              <Tr>
                <Th>Measure Title</Th>
                <Th>HEDIS Code</Th>
                <Th isNumeric>Stars Weight</Th>
                <Th isNumeric>Open Alerts</Th>
                <Th isNumeric>At Risk</Th>
                <Th isNumeric>Closed Gaps</Th>
              </Tr>
            </Thead>
            <Tbody>
              {measures.map((m) => (
                <Tr key={m.code} _hover={{ bg: "gray.50" }}>
                  <Td fontWeight="medium" color="gray.800">{m.label}</Td>
                  <Td>
                    <Badge variant="subtle" colorScheme="blue">
                      {m.cms_hedis_id || "—"}
                    </Badge>
                  </Td>
                  <Td isNumeric fontWeight="bold" color="purple.600">{m.stars_weight ?? "—"}</Td>
                  <Td isNumeric>{m.open_gaps}</Td>
                  <Td isNumeric>
                    {m.at_risk > 0 ? (
                      <Badge colorScheme="red" variant="solid" borderRadius="md" px={2}>
                        {m.at_risk} Critical
                      </Badge>
                    ) : (
                      <Badge colorScheme="green" variant="subtle">Closed</Badge>
                    )}
                  </Td>
                  <Td isNumeric fontWeight="medium" color="green.600">{m.closed}</Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      </Box>

      {/* Alerts Feed */}
      <Box bg="white" borderRadius="2xl" boxShadow="xl" p={6}>
        <Text fontSize="lg" fontWeight="bold" mb={4} color="gray.800">
          Active Quality & Compliance Alerts
        </Text>

        {alerts.length === 0 ? (
          <Text color="gray.500" py={6} textAlign="center" fontSize="md">
            All care gaps closed! No active alerts found.
          </Text>
        ) : (
          <VStack align="stretch" spacing={6}>
            {/* Critical Alerts Section */}
            {criticalAlerts.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="red.600" textTransform="uppercase" mb={3} letterSpacing="0.05em">
                  🚨 Critical Severity Gaps ({criticalAlerts.length})
                </Text>
                <VStack align="stretch" spacing={3}>
                  {criticalAlerts.map(renderAlertCard)}
                </VStack>
              </Box>
            )}

            {/* High Alerts Section */}
            {highAlerts.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="orange.600" textTransform="uppercase" mb={3} letterSpacing="0.05em">
                  ⚠️ High Severity Alerts ({highAlerts.length})
                </Text>
                <VStack align="stretch" spacing={3}>
                  {highAlerts.map(renderAlertCard)}
                </VStack>
              </Box>
            )}

            {/* Medium/Low Alerts Section */}
            {mediumAlerts.length > 0 && (
              <Box>
                <Text fontSize="sm" fontWeight="bold" color="blue.600" textTransform="uppercase" mb={3} letterSpacing="0.05em">
                  ℹ️ Medium & Low Gaps ({mediumAlerts.length})
                </Text>
                <VStack align="stretch" spacing={3}>
                  {mediumAlerts.map(renderAlertCard)}
                </VStack>
              </Box>
            )}
          </VStack>
        )}
      </Box>

      {/* Medication Fills History Log */}
      <Box bg="white" borderRadius="2xl" boxShadow="xl" p={6}>
        <HStack justify="space-between" mb={4}>
          <Box>
            <Text fontSize="lg" fontWeight="bold" color="gray.800">
              Medication Dispensation & Refill History
            </Text>
            <Text fontSize="xs" color="gray.500">
              Recent medication fills from clinic manual entry and external integrations.
            </Text>
          </Box>
          <Button size="sm" leftIcon={<AddIcon />} colorScheme="blue" variant="outline" onClick={onFillOpen}>
            Add Fill Event
          </Button>
        </HStack>

        {loadingFills ? (
          <Spinner size="md" color="blue.500" />
        ) : fills.length === 0 ? (
          <Text fontSize="sm" color="gray.500" py={4} textAlign="center">
            No medication fill records logged. Click "Add Fill Event" to register manual pharmacy dates.
          </Text>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th>Patient Name</Th>
                  <Th>Drug Class</Th>
                  <Th>Medication Name</Th>
                  <Th>Fill Date</Th>
                  <Th>Days Supply</Th>
                  <Th>Expected Refill</Th>
                  <Th>Data Source</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {fills.map((f) => (
                  <Tr key={f.id} _hover={{ bg: "gray.50" }}>
                    <Td fontWeight="semibold">{f.patient_name || `Patient ID: ${f.patient_id?.substring(0, 8)}`}</Td>
                    <Td>
                      <Badge colorScheme="purple" variant="outline">
                        {f.med_class?.replace("_", " ").toUpperCase()}
                      </Badge>
                    </Td>
                    <Td fontSize="sm">{f.medication_name || f.ndc || "—"}</Td>
                    <Td>{f.filled_at?.substring(0, 10)}</Td>
                    <Td fontWeight="semibold">{f.days_supply || 30} days</Td>
                    <Td color="orange.700" fontWeight="medium">{f.expected_refill_at?.substring(0, 10)}</Td>
                    <Td>
                      <Badge colorScheme={f.source === "manual" ? "gray" : "green"}>
                        {f.source}
                      </Badge>
                    </Td>
                    <Td isNumeric>
                      <IconButton
                        size="xs"
                        colorScheme="red"
                        variant="ghost"
                        icon={<DeleteIcon />}
                        isLoading={deletingFillId === f.id}
                        onClick={() => handleDeleteFill(f.id)}
                        aria-label="Delete fill event"
                      />
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>

      {/* Modern Accordion: How Compliance Works */}
      <Box
        borderRadius="2xl"
        overflow="hidden"
        boxShadow="xl"
        border="1px solid"
        borderColor="purple.100"
      >
        <Box bgGradient="linear(135deg, #667eea 0%, #764ba2 100%)" px={6} py={4}>
          <HStack spacing={3}>
            <Box bg="whiteAlpha.200" borderRadius="xl" p={2} display="flex" alignItems="center" justifyContent="center">
              <Text fontSize="xl">🔬</Text>
            </Box>
            <Box>
              <Text color="white" fontWeight="bold" fontSize="lg" lineHeight={1.2}>
                How compliance monitoring works
              </Text>
              <Text color="whiteAlpha.800" fontSize="sm">
                AI-powered per-document quality gap scanning — end to end
              </Text>
            </Box>
          </HStack>
        </Box>

        <Box bg="white" px={2} pb={2}>
          <Accordion allowMultiple defaultIndex={[0]}>
            <AccordionItem border="none">
              <AccordionButton px={5} py={4} _hover={{ bg: "purple.50" }} borderRadius="lg">
                <HStack flex={1} textAlign="left" spacing={3}>
                  <Box bg="purple.50" borderRadius="full" w={8} h={8} display="flex" alignItems="center" justifyContent="center">
                    <Text fontSize="sm" fontWeight="bold" color="purple.600">1</Text>
                  </Box>
                  <Text fontWeight="semibold" color="gray.800">
                    Per-Document visit scanning — searching all SOAP notes & records
                  </Text>
                </HStack>
                <AccordionIcon color="purple.400" />
              </AccordionButton>
              <AccordionPanel pb={4} px={6}>
                <VStack align="stretch" spacing={2}>
                  <Text fontSize="sm" color="gray.600">
                    On each evaluation run, the engine fetches all patient records from your schema and queries all individual visit records (including transcripts, uploaded files, and AI-generated SOAP notes).
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Natural-language keyword lists (e.g. <Badge variant="subtle" colorScheme="purple">mammogram</Badge>, <Badge variant="subtle" colorScheme="purple">a1c</Badge>) are matched against all document fields to extract performed dates from the surrounding context, ensuring maximum accuracy for real-world clinics.
                  </Text>
                </VStack>
              </AccordionPanel>
            </AccordionItem>

            <Divider />

            <AccordionItem border="none">
              <AccordionButton px={5} py={4} _hover={{ bg: "blue.50" }} borderRadius="lg">
                <HStack flex={1} textAlign="left" spacing={3}>
                  <Box bg="blue.50" borderRadius="full" w={8} h={8} display="flex" alignItems="center" justifyContent="center">
                    <Text fontSize="sm" fontWeight="bold" color="blue.600">2</Text>
                  </Box>
                  <Text fontWeight="semibold" color="gray.800">
                    HEDIS gap detection — comparing against catalog intervals
                  </Text>
                </HStack>
                <AccordionIcon color="blue.400" />
              </AccordionButton>
              <AccordionPanel pb={4} px={6}>
                <Text fontSize="sm" color="gray.600">
                  Each preventive measure is defined in the HEDIS / Stars catalog with a recommended recurrence interval (e.g. A1c every 6 months, colonoscopy every 10 years). If the last-found date falls outside that window, the patient is flagged as overdue.
                </Text>
              </AccordionPanel>
            </AccordionItem>

            <Divider />

            <AccordionItem border="none">
              <AccordionButton px={5} py={4} _hover={{ bg: "teal.50" }} borderRadius="lg">
                <HStack flex={1} textAlign="left" spacing={3}>
                  <Box bg="teal.50" borderRadius="full" w={8} h={8} display="flex" alignItems="center" justifyContent="center">
                    <Text fontSize="sm" fontWeight="bold" color="teal.600">3</Text>
                  </Box>
                  <Text fontWeight="semibold" color="gray.800">
                    Medication adherence — detecting refill gaps
                  </Text>
                </HStack>
                <AccordionIcon color="teal.400" />
              </AccordionButton>
              <AccordionPanel pb={4} px={6}>
                <Text fontSize="sm" color="gray.600">
                  Critical drug classes are tracked for refill recency. If a refill note or prescription mention hasn't appeared within the configured grace window (default 14 days past expected date), a medication non-adherence alert is raised.
                </Text>
              </AccordionPanel>
            </AccordionItem>
          </Accordion>
        </Box>
      </Box>

      {/* Medication Fill Modal */}
      <MedicationFillModal isOpen={isFillOpen} onClose={onFillClose} onSaved={refresh} />
    </VStack>
  );

  // Helper to render alert cards beautifully
  function renderAlertCard(a) {
    return (
      <Box
        key={a.id}
        p={4}
        borderWidth="1px"
        borderRadius="xl"
        borderColor="gray.250"
        borderLeft="6px solid"
        borderLeftColor={`${severityColor[a.severity]}.500`}
        boxShadow="md"
        _hover={{ shadow: "lg", bg: "gray.50" }}
        transition="all 0.2s"
      >
        <HStack justify="space-between" align="start" mb={2}>
          <Box flex={1}>
            <HStack mb={2}>
              <Badge colorScheme={severityColor[a.severity]} px={2} borderRadius="md">
                {a.severity}
              </Badge>
              <Badge variant="outline" px={2} borderRadius="md">
                {a.alert_type === "medication" ? "Medication Non-Adherence" : "Care Gap"}
              </Badge>
              {a.due_date && (
                <Badge colorScheme="purple" variant="solid" px={2} borderRadius="md">
                  Due: {a.due_date}
                </Badge>
              )}
            </HStack>
            <Text fontWeight="bold" fontSize="md" color="gray.800">{a.title}</Text>
            <Text fontSize="sm" color="gray.650" mt={1}>
              <strong>Patient:</strong> {a.patient_name || a.patient_id}
              {a.provider_name ? ` · Provider: ${a.provider_name}` : ""}
            </Text>
            <Text fontSize="sm" color="gray.600" mt={1}>
              {a.description}
            </Text>
          </Box>
        </HStack>
        <HStack spacing={2} flexWrap="wrap" mt={3} pt={2} borderTop="1px solid" borderColor="gray.100">
          {a.alert_type === "preventive" && (
            <Button
              size="xs"
              colorScheme="purple"
              isLoading={actionId === a.id}
              onClick={() =>
                runAction(
                  a.id,
                  (id) => orderComplianceTest(id, true),
                  "Test order queued",
                )
              }
            >
              Order test (1-tap)
            </Button>
          )}
          <Button
            size="xs"
            colorScheme="blue"
            isLoading={actionId === a.id}
            onClick={() =>
              runAction(
                a.id,
                createComplianceOutreachTask,
                "Outreach task created",
              )
            }
          >
            Create outreach task
          </Button>
          <Button
            size="xs"
            variant="outline"
            isLoading={actionId === a.id}
            onClick={() =>
              runAction(
                a.id,
                documentComplianceChart,
                "Documented in chart",
              )
            }
          >
            Document chart
          </Button>
          <Button
            size="xs"
            variant="ghost"
            isLoading={actionId === a.id}
            onClick={() =>
              runAction(
                a.id,
                acknowledgeComplianceAlert,
                "Acknowledged",
              )
            }
          >
            Acknowledge
          </Button>
          <Button
            size="xs"
            colorScheme="teal"
            variant="outline"
            isLoading={actionId === a.id}
            onClick={() =>
              runAction(
                a.id,
                notifyCompliancePatient,
                "Patient notified",
              )
            }
          >
            Notify patient
          </Button>
          <Button
            size="xs"
            variant="ghost"
            isLoading={actionId === a.id}
            onClick={() =>
              runAction(a.id, resolveComplianceAlert, "Resolved")
            }
          >
            Resolve
          </Button>
        </HStack>
      </Box>
    );
  }
};

export default ComplianceQualityDashboard;
